import * as React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { DayPicker } from 'react-day-picker';

import { cn } from 'src/lib/utils';

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

// Shared reset for every <button> rdp renders — we disabled tailwind's preflight
// (to avoid clobbering MUI), so we have to neutralize the browser defaults ourselves.
const buttonReset =
  'appearance-none border-0 bg-transparent p-0 cursor-pointer outline-none focus:outline-none';

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'flex flex-col sm:flex-row gap-4',
        month: 'space-y-4',
        caption: 'flex justify-center pt-1 relative items-center',
        caption_label: 'text-sm font-medium text-foreground',
        nav: 'space-x-1 flex items-center',
        nav_button: cn(
          buttonReset,
          'inline-flex h-7 w-7 items-center justify-center rounded-md text-grey-600 hover:bg-grey-100 hover:text-grey-800 transition-colors'
        ),
        nav_button_previous: 'absolute left-1',
        nav_button_next: 'absolute right-1',
        table: 'w-full border-collapse',
        head_row: 'flex',
        head_cell: 'text-grey-500 w-9 h-8 font-normal text-[0.75rem] flex items-center justify-center',
        row: 'flex w-full mt-1',
        cell: cn(
          'relative p-0 h-9 w-9 text-center text-sm focus-within:relative focus-within:z-20',
          // middle of a range: solid pill background that connects across cells
          '[&:has(.day-range-middle)]:bg-primary-lighter',
          // start cell: fill only the right half so the circle endpoint sits flush with the range
          '[&:has(.day-range-start)]:bg-gradient-to-r [&:has(.day-range-start)]:from-transparent [&:has(.day-range-start)]:from-50% [&:has(.day-range-start)]:to-primary-lighter [&:has(.day-range-start)]:to-50%',
          // end cell: fill only the left half
          '[&:has(.day-range-end)]:bg-gradient-to-r [&:has(.day-range-end)]:from-primary-lighter [&:has(.day-range-end)]:from-50% [&:has(.day-range-end)]:to-transparent [&:has(.day-range-end)]:to-50%',
          // start == end (single-day selection or pre-end state): drop the half-fill
          '[&:has(.day-range-start.day-range-end)]:bg-none'
        ),
        day: cn(
          buttonReset,
          'inline-flex h-9 w-9 items-center justify-center rounded-full font-normal text-grey-800 transition-colors',
          'hover:bg-grey-100 aria-selected:opacity-100'
        ),
        day_range_start:
          'day-range-start !bg-primary-dark !text-primary-foreground hover:!bg-primary-darker',
        day_range_end:
          'day-range-end !bg-primary-dark !text-primary-foreground hover:!bg-primary-darker',
        day_selected:
          '!bg-primary-dark !text-primary-foreground hover:!bg-primary-darker',
        day_today: 'font-semibold text-primary',
        day_outside: 'day-outside text-grey-400',
        day_disabled: 'text-grey-300 cursor-not-allowed hover:bg-transparent',
        day_range_middle:
          'day-range-middle !rounded-none !bg-transparent !text-grey-800 hover:!bg-primary-lighter',
        day_hidden: 'invisible',
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
      }}
      {...props}
    />
  );
}
Calendar.displayName = 'Calendar';

export { Calendar };
